import { useState } from 'react'
import { TabsContent } from '@design-system/components/tabs'
import { Skeleton } from '@design-system/components/skeleton'
import { ROUTES } from '@/routes/routes'
import { TooltipProvider } from '@/components/ui/tooltip'
import { type SubscriptionTier, type UserProfile, usersApi } from '@/lib/api'
import { isMeteringEnrolled } from '@/lib/billing/metering'
import { toast } from 'sonner'
import { OverageControls } from './OverageControls'
import { BillingLinks } from './BillingLinks'
import { PlanSummaryCard } from './PlanSummaryCard'
import { PlanViolationBanner } from './PlanViolationBanner'
import { TeamUsageSection } from './TeamUsageSection'
import { UsageLogTable } from './UsageLogTable'
import { DailyUsageLog } from './DailyUsageLog'
import { downloadDailyUsageCsv } from './exportUsageCsv'
import { useCreditHistory, useDailyCreditUsage, useMemberUsage } from './useBillingData'
import { approxMaxSeats } from './planConfig'
import { creditRateCentsFromInfo } from './useCreditRateCents'
import { LockedFreeGate } from '@/components/billing/LockedFreeGate'
import { CreditScenarioModal } from './CreditScenarioModal'
import { HowCreditsWorkDialog } from './HowCreditsWorkDialog'
import { useTranslation } from 'react-i18next'

export interface BillingTabProps {
  profile: UserProfile | null
  onProfileUpdate: () => void
  onOpenPortal?: () => void
  openingPortal?: boolean
}

function isAdminRole(role: string | undefined | null): boolean {
  return role === 'owner' || role === 'admin'
}

export function BillingTab(props: BillingTabProps) {
  const { profile, onProfileUpdate } = props
  const { t } = useTranslation('app')

  const tier = (profile?.subscriptionTier ?? null) as SubscriptionTier | null
  const orgId = profile?.organizationToken
  const isAdmin = isAdminRole(profile?.orgRole) || (profile?.creditInfo?.isOrgOwner ?? false)
  const interval = profile?.billingInterval ?? null
  const hasActiveSubscription = profile?.hasActiveSubscription ?? false

  // Hooks must be called unconditionally, before any early return.
  const memberUsage = useMemberUsage(isAdmin && profile ? orgId : undefined)

  // Fetch the largest window the API allows in one shot so the table can
  // group consecutive runs (chat sessions, etc.) without page boundaries
  // splitting them. Pagination over groups happens client-side inside
  // <UsageLogTable />.
  const USAGE_FETCH_LIMIT = 100
  /* Credit metering: an enrolled org (`on`) reads the daily usage API instead of the raw
     transaction list; everyone else keeps today's log exactly. */
  const isEnrolled = isMeteringEnrolled(profile?.creditInfo)
  const creditHistory = useCreditHistory(!!profile && !isEnrolled, {
    limit: USAGE_FETCH_LIMIT,
    offset: 0,
  })
  const [usagePage, setUsagePage] = useState(1)
  const [isExportingUsage, setIsExportingUsage] = useState(false)
  // Admins can switch the log between their own usage and the whole team's.
  const [usageScope, setUsageScope] = useState<'me' | 'team'>('me')
  const effectiveScope = isAdmin ? usageScope : 'me'
  const dailyUsage = useDailyCreditUsage(!!profile && isEnrolled, { page: usagePage, scope: effectiveScope })
  const teamUsage = useDailyCreditUsage(!!profile && isEnrolled && isAdmin, {
    page: 1,
    pageSize: 1,
    scope: 'team',
  })

  async function exportDailyUsage() {
    if (!dailyUsage.data) return
    setIsExportingUsage(true)
    try {
      await downloadDailyUsageCsv(usersApi.getDailyCreditUsageCsv, {
        from: dailyUsage.data.from,
        to: dailyUsage.data.to,
        scope: effectiveScope,
      })
    } catch {
      toast.error(t('billing.usage.exportFailed'))
    } finally {
      setIsExportingUsage(false)
    }
  }

  const members = memberUsage.data ?? []
  // One per-org overage credit rate (set in the backoffice) prices every dollar figure on this
  // tab, including "Billed at" and over-plan dollars. 0 hides the dollar lines, never a guess.
  const creditValueCents = creditRateCentsFromInfo(profile?.creditInfo) ?? 0
  const overageRateCents = creditValueCents

  const userName = profile?.name || profile?.email || t('billing.you')

  const [scenarioOpen, setScenarioOpen] = useState(false)
  const [creditsOpen, setCreditsOpen] = useState(false)

  const planViolation = profile?.planViolation === true

  const maxSeats = approxMaxSeats(tier)

  // Skeleton-gate: render after all hooks have been called.
  if (!profile) {
    return (
      <TabsContent value="billing" className="space-y-6">
        <Skeleton className="h-72 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </TabsContent>
    )
  }

  return (
    <TabsContent value="billing" className="space-y-6">
      {/* Single Provider for the whole tab — Radix expects one near the root */}
      <TooltipProvider delayDuration={150}>
      {tier === 'locked_free' && (
        <LockedFreeGate
          inline
          action={t('billing.gatedAction')}
          ctaLabel={t('billing.upgrade')}
        />
      )}

      <PlanViolationBanner
        show={planViolation}
        isAdmin={isAdmin}
        maxSeats={maxSeats}
        memberCount={members.length || 1}
        showUpgradeCta={hasActiveSubscription}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <div className="flex flex-col gap-3 lg:col-span-5">
          <PlanSummaryCard
            tier={tier as SubscriptionTier}
            creditInfo={profile.creditInfo}
            isPoolBilling={profile.creditInfo?.isPoolBilling ?? false}
            daysUntilReset={profile.daysUntilReset ?? 0}
            renewsAt={profile.renewsAt ?? null}
            trialEndsAt={profile.trialEndsAt ?? null}
            endsAt={profile.endsAt ?? null}
            lsStatus={profile.lsStatus ?? null}
            overageRateCents={overageRateCents}
          />
        </div>

        <div className="flex flex-col gap-4 lg:col-span-7">
          {orgId || !isAdmin ? (
            <div data-testid="billing-overage-card" className="rounded-xl bg-muted p-5">
              <OverageControls
                orgId={orgId ?? ''}
                allowOverage={profile.creditInfo?.allowOverage ?? false}
                defaultOverageLimitCents={profile.creditInfo?.overageLimitCents ?? null}
                onChange={onProfileUpdate}
                readOnly={!isAdmin}
                overageRateCents={overageRateCents}
              />
            </div>
          ) : null}
          <BillingLinks onOpenScenario={() => setScenarioOpen(true)} onOpenCredits={() => setCreditsOpen(true)} />
        </div>
      </div>

      {isEnrolled ? (
        dailyUsage.error ? (
          <div className="rounded-2xl border border-destructive/20 bg-destructive/5 p-6 text-center text-sm text-muted-foreground">
            {t('billing.loadUsageFailed')}
          </div>
        ) : (
          <DailyUsageLog
            data={dailyUsage.data}
            loading={dailyUsage.isLoading}
            viewerFallbackDisplay={userName}
            memberNames={Object.fromEntries(
              (teamUsage.data?.team?.members ?? []).map((member) => [member.userToken, member.displayName]),
            )}
            page={usagePage}
            onPageChange={setUsagePage}
            scope={isAdmin ? usageScope : undefined}
            onScopeChange={
              isAdmin
                ? (next) => {
                    setUsageScope(next)
                    setUsagePage(1)
                  }
                : undefined
            }
            onExport={() => void exportDailyUsage()}
            isExporting={isExportingUsage}
          />
        )
      ) : creditHistory.error ? (
        <div className="rounded-2xl border border-destructive/20 bg-destructive/5 p-6 text-center text-sm text-muted-foreground">
          {t('billing.loadUsageFailed')}
        </div>
      ) : (
        <UsageLogTable
          transactions={creditHistory.data?.items ?? []}
          loading={creditHistory.isLoading}
          viewerFallbackDisplay={userName}
          currentUserId={profile.id}
          costPerCreditCents={creditValueCents}
          showCost={(profile?.creditInfo?.overageUsed ?? 0) > 0}
        />
      )}

      {isAdmin && (
        memberUsage.error ? (
          <div className="rounded-2xl border border-destructive/20 bg-destructive/5 p-6 text-center text-sm text-muted-foreground">
            {t('billing.loadTeamFailed')}
          </div>
        ) : (
          <TeamUsageSection
            members={members}
            loading={memberUsage.isLoading}
            manageTeamHref={ROUTES.settingsTab('account')}
            overageRateCents={creditValueCents}
            daily={isEnrolled && teamUsage.data?.team ? teamUsage.data.team : null}
          />
        )
      )}
      <CreditScenarioModal
        open={scenarioOpen}
        onOpenChange={setScenarioOpen}
        interval={interval}
        enrolled={isEnrolled}
      />
      <HowCreditsWorkDialog open={creditsOpen} onOpenChange={setCreditsOpen} enrolled={isEnrolled} />
      </TooltipProvider>
    </TabsContent>
  )
}

export default BillingTab
